import React, { FormEvent, useEffect } from 'react';
import Btn, { BtnModifier } from '../../../../molecules/Btn';
import { ButtonType } from '../../../../../types/ButtonType';
import { Icons } from '../../../../atoms/SVG';
import { TooltipPlacement } from '../../../../atoms/Tooltip';
import Form from '../../../../molecules/Form/Form';
import { FormContainerModifier } from '../../../../molecules/Form';
import { CollapsibleContent } from '../../../CollapsibleOverlay/CollapsibleOverlay';
import useCollapsibleOverlay from '../../../../../hooks/useCollapsibleOverlay';
import IForm from '../../../../../types/IForm';
import { AddContentBlockFormTemplate } from './AddContentBlockFormTemplate';
import { ActionState, ErrorState } from '../../../../blocks/ContentWrapper/useContentWrapper';
import { useSonTranslation } from '../../../../../translation/useSonTranslation';
import { useHtmlValidId } from '../../../../../hooks/useHtmlValidId';
import {
  ContentWrapperAction,
  ContentWrapperBlocks,
  ContentWrapperError,
} from '../../../../blocks/ContentWrapper/ContentWrapper.types';
import { windowLogger } from '../../../../../_lib/Logger/windowLogger';

export type AddContentBlockFormProps = {
  uuid?: string;
  form: IForm;
  addBlock: (newBlock: ContentWrapperBlocks, position: number) => void;
  setAction(action: ActionState | null): void;
  setError: React.Dispatch<React.SetStateAction<ErrorState | null>>;
  positionString?: string;
  position: number;
};

export function AddContentBlockForm({
  uuid,
  form,
  addBlock,
  setAction,
  setError,
  position,
  positionString,
}: AddContentBlockFormProps) {
  const { t } = useSonTranslation('AddContentBlockForm');
  const uId = useHtmlValidId();
  const { collapsibleOpen, toggleCollapsible, toggleProps, collapsibleProps, popperInstance } =
    useCollapsibleOverlay('bottom');

  useEffect(() => {
    if (collapsibleOpen && popperInstance) {
      popperInstance.update();
    }
  });

  const onSubmit = async (event: FormEvent<HTMLFormElement>, model: any) => {
    try {
      event.preventDefault();

      setAction({ uuid, type: ContentWrapperAction.addBlock });

      const response = await model.submit(() => {
        // 1. In FF and Chrome we have a SyntheticEvent which has the nativeEvent property.
        // 2. In Safari it seems we have the nativeEvent itself but there's no submitter property.
        // 3. Safari succeeds with new FormData(event.target) which is the form
        //    tag and the supported API/way to go
        //    Safari further evaluates the call ^ to the actual clicked submit
        //    button (note we have several)
        // => FF and Chrome are failing here, but succeed with the submitter
        //    property (which is in fact the clicked button)
        // @ts-expect-error form typing is quirky
        const submitter = event.submitter || event.nativeEvent?.submitter;
        if (submitter) {
          const formData = new FormData();
          formData.append(submitter.name, submitter.value);
          return formData;
        }
        return new FormData(event.currentTarget);
      });

      addBlock(response.data, position);
      setAction(null);
      setError(null);
    } catch (e) {
      windowLogger.warn('Could not add new Block', e);

      setAction(null);
      setError({ uuid, type: ContentWrapperError.addBlock });
    }

    toggleCollapsible();
  };

  return (
    <>
      <Btn
        reference={toggleProps.ref}
        onClick={toggleProps.onClick}
        onKeyDown={toggleProps.onKeyDown}
        onKeyUp={toggleProps.onKeyUp}
        tooltipPlacement={TooltipPlacement.TOP}
        type={ButtonType.BUTTON}
        svgSpriteId={collapsibleOpen ? Icons.DECLINE_CROSS : Icons.ADD_PLUS}
        // The addBlock buttons in ContentWrapper blocks all have uuid and addBlock itself has no uuid
        text={uuid ? t('buttonWithPosition', { position: positionString || '' }) : t('button')}
        titleText={uuid ? t('buttonWithPosition', { position: positionString || '' }) : t('button')}
        hiddenText
        ariaControls={toggleProps['aria-controls']}
        popoverTarget={toggleProps.popoverTarget}
        popoverTargetAction={toggleProps.popoverTargetAction}
        modifier={
          collapsibleOpen
            ? [BtnModifier.MINOR_ACTION, BtnModifier.TRANSPARENT, BtnModifier.ACTIVE]
            : [BtnModifier.MINOR_ACTION, BtnModifier.TRANSPARENT]
        }
      />
      <CollapsibleContent {...collapsibleProps}>
        <div className="add-content-block-form__panel-box">
          <Form
            id={`content-wrapper-add-block-form-${uId}`}
            containerModifier={FormContainerModifier.NO_PADDING}
            model={form}
            className="add-content-block-form__form"
            onSubmit={onSubmit}
            allowNavigationOnUnsavedChanges
          >
            <ul className="add-content-block-form__list">
              <AddContentBlockFormTemplate />
            </ul>
          </Form>
        </div>
      </CollapsibleContent>
    </>
  );
}
